import type { JSX, ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { StatusDot, type StatusTone } from './StatusDot'

/**
 * Чип: короткое значение с необязательным статусом. Используется для состояний
 * ботов, версий, «онлайн/офлайн» и любых меток в один-два слова.
 *
 * Правило проекта, которое здесь зашито: если чип обозначает состояние, у него
 * ВСЕГДА есть точка и текст — цвет не может быть единственным носителем смысла
 * (дальтонизм, скриншот в ч/б, плохой монитор).
 */
export interface ChipProps {
  children: ReactNode
  tone?: StatusTone | 'neutral' | 'honey'
  /** Показать статусную точку слева. Для `neutral`/`honey` не имеет смысла. */
  dot?: boolean
  pulse?: boolean
  mono?: boolean
  className?: string
  title?: string
}

const TONE: Record<NonNullable<ChipProps['tone']>, string> = {
  neutral: 'border-line bg-panel-2 text-ink-muted',
  honey: 'border-honey/40 bg-honey/12 text-honey',
  live: 'border-live/35 bg-live/12 text-live',
  warn: 'border-warn/35 bg-warn/12 text-warn',
  busy: 'border-busy/35 bg-busy/12 text-busy',
  hold: 'border-hold/35 bg-hold/12 text-hold',
  idle: 'border-line bg-panel-2 text-ink-muted',
  fail: 'border-fail/35 bg-fail/12 text-fail'
}

const STATUS_TONES = new Set(['live', 'warn', 'busy', 'hold', 'idle', 'fail'])

export function Chip({
  children,
  tone = 'neutral',
  dot = false,
  pulse = false,
  mono = false,
  className,
  title
}: ChipProps): JSX.Element {
  const showDot = dot && STATUS_TONES.has(tone)
  return (
    <span
      title={title}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-pill border px-2 py-0.5 text-2xs whitespace-nowrap',
        TONE[tone],
        mono && 'font-mono tabular-nums',
        className
      )}
    >
      {showDot ? <StatusDot tone={tone as StatusTone} pulse={pulse} /> : null}
      {children}
    </span>
  )
}
